import type {
  BuildMediaSourceGroupsOptions,
  BuildReplaceSourceOptionsParams,
  MediaSourceGroupNode,
  MediaSourceLeaf,
  MediaSourceOption,
  ReplaceAiPanelKey,
  ReplaceSourceOption,
} from '@Components/media-source-menu/media-source-menu.types';
import {MediaSourceGroup, MediaSourceKind, ReplaceMediaKind} from '@Components/media-source-menu/media-source-menu.types';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import {isIconImageItem, isItemStickerImage} from '@Libraries/image-item.library';
import {assertNever} from '@Utils/assert-never.util';

interface ReplaceAiEntry {
  aiPanel: ReplaceAiPanelKey;
  icon: string;
  labelKey: string;
  subTextKey: string;
}

const ALL_GROUPS = [MediaSourceGroup.IMAGE, MediaSourceGroup.VIDEO];
const ALL_KINDS = [MediaSourceKind.UPLOADS, MediaSourceKind.STOCK, MediaSourceKind.AI];

const isImageGroup = (group: MediaSourceGroup): boolean => {
  return group === MediaSourceGroup.IMAGE;
};

const getGroupLabel = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_image') : window.i18next.t('pmwjs_video');
};

const getGroupIcon = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? 'icon-image' : 'icon-video';
};

const getGroupSubText = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_add_an_image_background') : window.i18next.t('pmwjs_add_a_video_background');
};

const getStockLabel = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_stock_photo') : window.i18next.t('pmwjs_stock_videos');
};

const getStockIcon = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? 'icon-stock-image' : 'icon-stock-video';
};

const getStockSubText = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_search_add_stock_photos') : window.i18next.t('pmwjs_search_add_stock_videos');
};

const getAiLabel = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_ai_images') : window.i18next.t('pmwjs_ai_videos');
};

const getAiIcon = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? 'icon-ai-image' : 'icon-video-ai';
};

const getUploadsRowContent = (): {text: string; icon: string; subText: string} => {
  return {text: window.i18next.t('pmwjs_uploads'), icon: 'icon-upload', subText: window.i18next.t('pmwjs_add_from_your_uploads')};
};

const buildOption = (group: MediaSourceGroup, kind: MediaSourceKind, options: BuildMediaSourceGroupsOptions): MediaSourceOption => {
  const leaf = {group, kind};
  const base = {
    leaf,
    id: `media-source-${group}-${kind}`,
    isAi: kind === MediaSourceKind.AI,
    onSelect: (): void => {
      options.onSelect(leaf);
    },
  };

  switch (kind) {
    case MediaSourceKind.UPLOADS:
      return {...base, ...getUploadsRowContent()};
    case MediaSourceKind.STOCK:
      return {...base, text: getStockLabel(group), icon: getStockIcon(group), subText: getStockSubText(group)};
    case MediaSourceKind.AI:
      return {
        ...base,
        text: getAiLabel(group),
        icon: getAiIcon(group),
        subText: window.i18next.t('pmwjs_create_with_ai'),
      };
    default:
      return assertNever(kind);
  }
};

/**
 * The canonical background menu: Image / Video, each expanding to Uploads, stock, and AI.
 * Every background surface renders this same tree so the option set cannot drift between them.
 */
export const buildMediaSourceGroups = (options: BuildMediaSourceGroupsOptions): MediaSourceGroupNode[] => {
  const groups = options.groups ?? ALL_GROUPS;
  const kinds = options.kinds ?? ALL_KINDS;

  return groups.map((group): MediaSourceGroupNode => {
    return {
      group,
      id: `media-source-group-${group}`,
      text: getGroupLabel(group),
      icon: getGroupIcon(group),
      subText: getGroupSubText(group),
      options: kinds.map((kind) => {
        return buildOption(group, kind, options);
      }),
    };
  });
};



const CREATE_WITH_AI_SUB_TEXT_KEY = 'pmwjs_create_with_ai';

const AI_IMAGES_REPLACE_ENTRY: ReplaceAiEntry = {aiPanel: 'ai-images', icon: 'icon-ai-image', labelKey: 'pmwjs_ai_images', subTextKey: CREATE_WITH_AI_SUB_TEXT_KEY};
const AI_VIDEOS_REPLACE_ENTRY: ReplaceAiEntry = {aiPanel: 'ai-videos', icon: 'icon-video-ai', labelKey: 'pmwjs_ai_videos', subTextKey: CREATE_WITH_AI_SUB_TEXT_KEY};
const AI_STICKERS_REPLACE_ENTRY: ReplaceAiEntry = {aiPanel: 'ai-stickers', icon: 'icon-ai-sticker', labelKey: 'pmwjs_ai_stickers_caps', subTextKey: CREATE_WITH_AI_SUB_TEXT_KEY};
const AI_VOICE_REPLACE_ENTRY: ReplaceAiEntry = {aiPanel: 'ai-voice', icon: 'icon-ai-voice', labelKey: 'pmwjs_ai_voice', subTextKey: 'pmwjs_ai_voice_list_item_desc'};

// The replaced item's own medium leads; a sticker only ever becomes another sticker, and icons
// offer no AI generators — their Explore media panel already carries the AI carousel.
const REPLACE_AI_ENTRIES_BY_MEDIA_KIND: Record<ReplaceMediaKind, ReplaceAiEntry[]> = {
  [ReplaceMediaKind.IMAGE]: [AI_IMAGES_REPLACE_ENTRY, AI_VIDEOS_REPLACE_ENTRY],
  [ReplaceMediaKind.VIDEO]: [AI_VIDEOS_REPLACE_ENTRY, AI_IMAGES_REPLACE_ENTRY],
  [ReplaceMediaKind.STICKER]: [AI_STICKERS_REPLACE_ENTRY],
  [ReplaceMediaKind.ICON]: [],
  [ReplaceMediaKind.AUDIO]: [AI_VOICE_REPLACE_ENTRY],
};



/**
 * The flat replace menu every replace surface renders: Uploads, Explore media, then the AI
 * generators that fit the replaced item's medium. One level deep by design — the stock panel
 * behind Explore media offers every media type, so the menu doesn't need a type split.
 */
export const buildReplaceSourceOptions = (mediaKind: ReplaceMediaKind, {idPrefix, onSelect}: BuildReplaceSourceOptionsParams): ReplaceSourceOption[] => {
  const uploadsRow: ReplaceSourceOption = {
    id: `${idPrefix}-uploads`,
    icon: 'icon-upload',
    text: window.i18next.t('pmwjs_uploads'),
    subText: window.i18next.t('pmwjs_replace_with_uploads_media'),
    isAi: false,
    onSelect: (): void => {
      onSelect({kind: MediaSourceKind.UPLOADS});
    },
  };
  const exploreMediaRow: ReplaceSourceOption = {
    id: `${idPrefix}-media`,
    icon: 'icon-media',
    text: window.i18next.t('pmwjs_explore_media'),
    subText: window.i18next.t('pmwjs_replace_with_stock_media'),
    isAi: false,
    onSelect: (): void => {
      onSelect({kind: MediaSourceKind.STOCK});
    },
  };
  const aiRows = REPLACE_AI_ENTRIES_BY_MEDIA_KIND[mediaKind].map((entry): ReplaceSourceOption => {
    return {
      id: `${idPrefix}-${entry.aiPanel}`,
      icon: entry.icon,
      text: window.i18next.t(entry.labelKey),
      subText: window.i18next.t(entry.subTextKey),
      isAi: true,
      onSelect: (): void => {
        onSelect({kind: MediaSourceKind.AI, aiPanel: entry.aiPanel});
      },
    };
  });

  return [uploadsRow, exploreMediaRow, ...aiRows];
};

/** Which flat replace menu a poster item (or slideshow slide) gets. Surfaces with nothing resolved get the image menu. */
export const getReplaceMediaKindForItem = (item: Item | undefined): ReplaceMediaKind => {
  if (!item) {
    return ReplaceMediaKind.IMAGE;
  }
  if (item.isVideo() || item.isVideoSlide()) {
    return ReplaceMediaKind.VIDEO;
  }
  if (isIconImageItem(item)) {
    return ReplaceMediaKind.ICON;
  }
  if (isItemStickerImage(item)) {
    return ReplaceMediaKind.STICKER;
  }
  return ReplaceMediaKind.IMAGE;
};

/**
 * The top-level Uploads shortcut every background picker offers above its Image / Video branches,
 * rendered via MediaSourceMenu's `leadingOptions`. Its group-less leaf routes to the uploads modal
 * that lists image AND video backgrounds, so one row covers both without a third level.
 */
export const buildTopLevelUploadsOption = (onSelect: (leaf: MediaSourceLeaf) => void): MediaSourceOption => {
  const leaf: MediaSourceLeaf = {group: null, kind: MediaSourceKind.UPLOADS};
  return {
    ...getUploadsRowContent(),
    leaf,
    id: 'media-source-uploads-top-level',
    isAi: false,
    onSelect: (): void => {
      onSelect(leaf);
    },
  };
};
